import {Component, OnInit} from '@angular/core';
import {ApiService} from '../../core/http/api.service';
import {DashboardService} from '../dashboard.service';
import {UtilsService} from '../../core/services/utils/utils.service';
import {forkJoin} from 'rxjs';
import {ActivatedRoute} from '@angular/router';

interface MarketplaceProjectRoleForm {
  id: string;
  name: string;
  description: string;
  isApplyEnabled: boolean;
  isFulfilled: boolean;
  sortOrder: number;
}

interface MarketplaceProjectForm {
  id: string;
  iconKey: string;
  name: string;
  description: string;
  websiteUrl: string | null;
  twitterUrl: string;
  isActive: boolean;
  roles: MarketplaceProjectRoleForm[];
}

@Component({
    selector: 'app-marketplace-projects',
    templateUrl: './marketplace-projects.component.html',
    styleUrls: ['./marketplace-projects.component.css'],
    standalone: false
})
export class MarketplaceProjectsComponent implements OnInit {

  loading = true;
  saving = false;
  uploadingIcon = false;
  showProjectModal = false;
  isEditMode = false;
  page = 1;
  perPage = 10;
  loaders = Array(10).fill(0);
  projects: any[] = [];
  filters = {
    keyword: '',
    status: 'all'
  };
  marketplaceType: 'project' | 'satire' = 'project';
  activeTabIndex = 1;
  projectForm: MarketplaceProjectForm = this.createEmptyProjectForm();

  constructor(
    private api: ApiService,
    private dashApi: DashboardService,
    private utils: UtilsService,
    private route: ActivatedRoute
  ) {
  }

  ngOnInit(): void {
    this.marketplaceType = this.route.snapshot?.data?.marketplaceType === 'satire' ? 'satire' : 'project';
    this.activeTabIndex = this.isSatire ? 2 : 1;
    this.projectForm = this.createEmptyProjectForm();
    this.getProjects();
  }

  get isSatire(): boolean {
    return this.marketplaceType === 'satire';
  }

  get entityLabel(): string {
    return this.isSatire ? 'Satire' : 'Project';
  }

  get entityLabelPlural(): string {
    return this.isSatire ? 'Satires' : 'Projects';
  }

  createEmptyRole(useSatireDefault = false): MarketplaceProjectRoleForm {
    return {
      id: '',
      name: this.isSatire && useSatireDefault ? 'Creator' : '',
      description: this.isSatire && useSatireDefault ? 'Create satire content for admin approval.' : '',
      isApplyEnabled: true,
      isFulfilled: false,
      sortOrder: 0,
    };
  }

  createEmptyProjectForm(): MarketplaceProjectForm {
    return {
      id: '',
      iconKey: '',
      name: '',
      description: '',
      websiteUrl: this.isSatire ? null : '',
      twitterUrl: '',
      isActive: true,
      roles: [this.createEmptyRole(true)]
    };
  }

  get filteredProjects(): any[] {
    const keyword = (this.filters.keyword || '').trim().toLowerCase();
    const status = this.filters.status;

    return (this.projects || []).filter(project => {
      const matchesKeyword = !keyword || [
        project?.name,
        project?.description,
        project?.websiteUrl,
        project?.twitterUrl,
      ].some(value => String(value || '').toLowerCase().includes(keyword));

      const isActive = project?.isActive !== false;
      const matchesStatus = status === 'all'
        || (status === 'active' && isActive)
        || (status === 'inactive' && !isActive);

      return matchesKeyword && matchesStatus;
    });
  }

  private extractListFromResponse(res: any): any[] {
    const direct = res?.data;
    if (Array.isArray(direct)) return direct;

    const response = res?.response || {};
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.projects)) return response.projects;
    if (Array.isArray(response?.satires)) return response.satires;
    if (Array.isArray(response?.items)) return response.items;

    return [];
  }

  private toSatireCategory(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private isSatireRecord(item: any): boolean {
    const category = this.toSatireCategory(item?.category || item?.marketplaceItemType);
    if (category === 'satire') return true;

    // Legacy/admin responses may omit category on satire endpoints.
    if (item?.websiteUrl === null || item?.websiteUrl === undefined) {
      return true;
    }

    return false;
  }

  private applySatireFallback(): void {
    this.api.getMarketplaceProjects({includeInactive: true}).subscribe(projectRes => {
      const fallbackItems = this.extractListFromResponse(projectRes)
        .filter(item => this.isSatireRecord(item));
      this.projects = fallbackItems;
      this.loading = false;
    }, fallbackErr => {
      this.loading = false;
      this.utils.resErr(fallbackErr);
    });
  }

  getProjects(): void {
    this.loading = true;
    const req$ = this.isSatire
      ? this.api.getMarketplaceSatires({includeInactive: true})
      : this.api.getMarketplaceProjects({includeInactive: true});

    req$.subscribe(res => {
      const list = this.extractListFromResponse(res);
      if (this.isSatire && !list.length) {
        this.applySatireFallback();
        return;
      }
      this.projects = list;
      this.loading = false;
    }, err => {
      if (this.isSatire) {
        this.applySatireFallback();
        return;
      }
      this.loading = false;
      this.utils.resErr(err);
    });
  }

  mapProjectToForm(project): MarketplaceProjectForm {
    const roles = Array.isArray(project?.roles) ? project.roles : [];
    return {
      id: project?.id || '',
      iconKey: project?.iconKey || '',
      name: project?.name || '',
      description: project?.description || '',
      websiteUrl: project?.websiteUrl || '',
      twitterUrl: project?.twitterUrl || '',
      isActive: project?.isActive !== false,
      roles: roles.length
        ? roles.map((role, index) => ({
          id: role?.id || '',
          name: role?.name || '',
          description: role?.description || '',
          isApplyEnabled: role?.isApplyEnabled !== false,
          isFulfilled: role?.isFulfilled === true,
          sortOrder: Number(role?.sortOrder ?? index),
        }))
        : [this.createEmptyRole()]
    };
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.projectForm = this.createEmptyProjectForm();
    this.showProjectModal = true;
  }

  openEditModal(project): void {
    this.isEditMode = true;
    this.projectForm = this.mapProjectToForm(project);
    this.showProjectModal = true;
  }

  closeProjectModal(): void {
    if (this.saving || this.uploadingIcon) {
      return;
    }
    this.showProjectModal = false;
    this.projectForm = this.createEmptyProjectForm();
    this.isEditMode = false;
  }

  uploadIcon(event): void {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    this.uploadingIcon = true;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      this.dashApi.uploadImage({file: reader.result}).subscribe(res => {
        this.projectForm.iconKey = res.response?.url || '';
        this.uploadingIcon = false;
      }, err => {
        this.uploadingIcon = false;
        this.utils.resErr(err);
      });
    };
  }

  handleIconUploadClick(): void {
    const ele = document.getElementById('project_icon_upload');
    if (!!ele) {
      ele.click();
    }
  }

  addRole(): void {
    this.projectForm.roles.push({
      ...this.createEmptyRole(),
      sortOrder: this.projectForm.roles.length
    });
  }

  removeRole(index: number): void {
    if (this.projectForm.roles.length === 1) {
      this.projectForm.roles[0] = this.createEmptyRole(this.isSatire);
      return;
    }
    this.projectForm.roles.splice(index, 1);
  }

  sanitizeRoles(roles: MarketplaceProjectRoleForm[] = []): MarketplaceProjectRoleForm[] {
    return (roles || [])
      .map((role, index) => ({
        id: role?.id || '',
        name: String(role?.name || '').trim(),
        description: String(role?.description || '').trim(),
        isApplyEnabled: role?.isApplyEnabled !== false,
        isFulfilled: role?.isFulfilled === true,
        sortOrder: Number.isFinite(Number(role?.sortOrder)) ? Number(role?.sortOrder) : index,
      }))
      .filter(role => role.name && role.description);
  }

  validateForm(): boolean {
    if (!this.projectForm.iconKey) {
      this.utils.toastError(`Please upload a ${this.entityLabel.toLowerCase()} icon`);
      return false;
    }
    if (!(this.projectForm.name || '').trim()) {
      this.utils.toastError(`Please enter ${this.entityLabel.toLowerCase()} name`);
      return false;
    }
    if (!(this.projectForm.description || '').trim()) {
      this.utils.toastError(`Please enter ${this.entityLabel.toLowerCase()} description`);
      return false;
    }
    if (!this.isSatire && !(this.projectForm.websiteUrl || '').trim()) {
      this.utils.toastError('Please enter project website URL');
      return false;
    }
    return true;
  }

  buildProjectPayload(withRoles = false): any {
    const payload: any = {
      iconKey: (this.projectForm.iconKey || '').trim(),
      name: (this.projectForm.name || '').trim(),
      description: (this.projectForm.description || '').trim(),
      category: this.isSatire ? 'satire' : 'project',
      websiteUrl: this.isSatire
        ? null
        : (this.projectForm.websiteUrl || '').trim(),
      twitterUrl: (this.projectForm.twitterUrl || '').trim() || null,
      isActive: this.projectForm.isActive !== false,
    };

    if (withRoles) {
      payload.roles = this.sanitizeRoles(this.projectForm.roles).map(role => ({
        name: role.name,
        description: role.description,
        isApplyEnabled: role.isApplyEnabled,
        isFulfilled: role.isFulfilled,
        sortOrder: role.sortOrder
      }));
    }

    return payload;
  }

  syncRoles(projectId: string): any[] {
    const roles = this.sanitizeRoles(this.projectForm.roles);
    if (!roles.length) {
      return [];
    }

    return roles.map(role => {
      const payload = {
        name: role.name,
        description: role.description,
        isApplyEnabled: role.isApplyEnabled,
        isFulfilled: role.isFulfilled,
        sortOrder: role.sortOrder
      };

      if (role.id) {
        return this.isSatire
          ? this.api.updateMarketplaceSatireRole(projectId, role.id, payload)
          : this.api.updateMarketplaceProjectRole(projectId, role.id, payload);
      }

      return this.isSatire
        ? this.api.createMarketplaceSatireRole(projectId, payload)
        : this.api.createMarketplaceProjectRole(projectId, payload);
    });
  }

  saveProject(): void {
    if (!this.validateForm() || this.saving) {
      return;
    }

    this.saving = true;

    if (!this.isEditMode || !this.projectForm.id) {
      const payload = this.buildProjectPayload(true);
      const req$ = this.isSatire
        ? this.api.createMarketplaceSatire(payload)
        : this.api.createMarketplaceProject(payload);
      req$.subscribe(() => {
        this.saving = false;
        this.utils.toastSuccess(`${this.entityLabel} created successfully`);
        this.closeProjectModal();
        this.getProjects();
      }, err => {
        this.saving = false;
        this.utils.resErr(err);
      });
      return;
    }

    const payload = this.buildProjectPayload(false);
    const req$ = this.isSatire
      ? this.api.updateMarketplaceSatire(this.projectForm.id, payload)
      : this.api.updateMarketplaceProject(this.projectForm.id, payload);
    req$.subscribe(() => {
      const roleRequests = this.syncRoles(this.projectForm.id);
      if (!roleRequests.length) {
        this.saving = false;
        this.utils.toastSuccess(`${this.entityLabel} updated successfully`);
        this.closeProjectModal();
        this.getProjects();
        return;
      }

      forkJoin(roleRequests).subscribe(() => {
        this.saving = false;
        this.utils.toastSuccess(`${this.entityLabel} and roles updated successfully`);
        this.closeProjectModal();
        this.getProjects();
      }, err => {
        this.saving = false;
        this.utils.resErr(err);
      });
    }, err => {
      this.saving = false;
      this.utils.resErr(err);
    });
  }

  deleteProject(project): void {
    if (!project?.id) {
      this.utils.toastError(`Invalid ${this.entityLabel.toLowerCase()} record`);
      return;
    }

    const label = String(project?.name || this.entityLabel);
    const confirmDelete = confirm(
      `Are you sure you want to delete "${label}"? This will permanently remove the record from database.`
    );
    if (!confirmDelete) return;

    const req$ = this.isSatire
      ? this.api.deleteMarketplaceSatire(project.id)
      : this.api.deleteMarketplaceProject(project.id);
    req$.subscribe(() => {
      this.utils.toastSuccess(`${this.entityLabel} deleted successfully`);
      this.getProjects();
    }, err => this.utils.resErr(err));
  }

  applyFilters(): void {
    this.page = 1;
  }

  clearFilters(): void {
    this.filters = {
      keyword: '',
      status: 'all'
    };
    this.page = 1;
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.keyword || '').trim()
      || this.filters.status !== 'all';
  }

  handleFilterInputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.applyFilters();
    }
  }

  pageChange(event): void {
    this.page = event;
    window.scrollTo(0, 0);
  }

  handleCopyToClipboard(str: any = ''): void {
    this.utils.copyToClipboard(str);
    this.utils.toastSuccess('ID copied to your clipboard successfully');
  }
}
